import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PLAN_URL from "../../../utils/PLAN_URL";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Box,
  Grid,
  Divider,
  IconButton,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import WarningIcon from "@mui/icons-material/Warning";
import { MaterialReactTable } from "material-react-table";
import styled from "styled-components";
import ReactECharts from "echarts-for-react";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const DashboardContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 16px;
`;

const StockChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const StatCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StatCardContent = styled(CardContent)`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px !important;
`;

const ChartContainer = styled(Paper)`
  padding: 16px;
  height: 500px;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ChartTabPanel = styled.div`
  display: ${(props) => (props.value === props.index ? "block" : "none")};
  height: calc(100% - 48px);
`;

const TrendChartContainer = styled(Paper)`
  padding: 16px;
  height: 400px;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SplitChartContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
`;

const BarChartContainer = styled(Box)`
  flex: 3;
  height: 100%;
`;

const PieChartContainer = styled(Box)`
  flex: 1;
  height: 100%;
`;

function Stock() {
  const { group_code, project_code } = useParams();
  const [systemDate, setSystemDate] = useState("");
  const [totalStockData, setTotalStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState("week");
  const [updateStatus, setUpdateStatus] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [inStockTrendData, setInStockTrendData] = useState([]);
  const [outStockTrendData, setOutStockTrendData] = useState([]);
  const [stockQuantityTrendData, setStockQuantityTrendData] = useState([]);
  const [trendDataLoading, setTrendDataLoading] = useState(false);
  useEffect(() => {
    fetchTrendData();
  }, [timeRange, group_code, project_code, updateStatus]);
  const fetchTrendData = async () => {
    setTrendDataLoading(true);
    try {
      let duration;
      switch (timeRange) {
        case "week":
          duration = 6;
          break;
        case "month":
          duration = 29;
          break;
        case "quarter":
          duration = 89;
          break;
        default:
          duration = 6;
      }
      const inStockResponse = await axios.get(
        PLAN_URL +
          `/api/stock/getTransition?group_code=${group_code}&project_code=${project_code}&direction=in&duration=${duration}`
      );
      const outStockResponse = await axios.get(
        PLAN_URL +
          `/api/stock/getTransition?group_code=${group_code}&project_code=${project_code}&direction=out&duration=${duration}`
      );
      const stockQuantityResponse = await axios.get(
        PLAN_URL +
          `/api/stock/getTransition?group_code=${group_code}&project_code=${project_code}&direction=stock&duration=${duration}`
      );
      setInStockTrendData(inStockResponse.data || []);
      setOutStockTrendData(outStockResponse.data || []);
      setStockQuantityTrendData(stockQuantityResponse.data || []);
    } catch (error) {
      console.error("추이 데이터 불러오기 실패:", error);
      openSnackbar("추이 데이터를 불러오지 못했습니다.", "error");
    } finally {
      setTrendDataLoading(false);
    }
  };
  const organizeTrendData = (data, direction = "in") => {
    const itemCodes = Array.from(new Set(data.map((item) => item.item_code)));
    const dateGroups = {};
    const allDates = [];
    data.forEach((item) => {
      if (!dateGroups[item.snapshot_date]) {
        dateGroups[item.snapshot_date] = {};
        allDates.push(item.snapshot_date);
      }
      let valueField;
      if (direction === "in") {
        valueField = "daily_stock_in";
      } else if (direction === "out") {
        valueField = "daily_stock_out";
      } else if (direction === "stock") {
        valueField = "total_stock_quantity";
      }
      dateGroups[item.snapshot_date][item.item_code] = parseInt(
        item[valueField] || 0
      );
    });
    allDates.sort();
    const series = itemCodes.map((itemCode) => {
      return {
        name: itemCode,
        type: "line",
        data: allDates.map((date) => dateGroups[date][itemCode] || 0),
      };
    });
    return { dates: allDates, series };
  };
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await axios.get(
          PLAN_URL +
            `/api/stock/getSystem?group_code=${group_code}&project_code=${project_code}`
        );
        const stockSummaryRes = await axios.get(
          PLAN_URL +
            `/api/stock/getSummary?group_code=${group_code}&project_code=${project_code}`
        );
        setSystemDate(res.data);
        const stockData = stockSummaryRes.data.map((item) => ({
          ...item,
          yesterday_quantity:
            item.total_stock_quantity -
            (item.changeQty || Math.floor(Math.random() * 50) - 25),
          min_stock_quantity: Math.floor(Math.random() * 100) + 50,
        }));
        setTotalStockData(stockData);
        const lowStock = stockData.filter(
          (item) => item.total_stock_quantity < item.min_stock_quantity
        );
        setLowStockItems(lowStock);
      } catch (e) {
        console.log(e);
        openSnackbar("자료를 불러오지 못했습니다.", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [group_code, project_code, updateStatus]);
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const formatQuantity = (quantity) => {
    return quantity.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " EA";
  };
  const renderChangeIndicator = (changeQty) => {
    const changeValue = Number(changeQty);
    let color, icon, formattedChange;
    if (changeValue > 0) {
      color = "#1976D2";
      icon = (
        <ArrowUpwardIcon fontSize="small" style={{ marginRight: "4px" }} />
      );
      formattedChange = `+${changeValue.toLocaleString()} EA`;
    } else if (changeValue < 0) {
      color = "#f44336";
      icon = (
        <ArrowDownwardIcon fontSize="small" style={{ marginRight: "4px" }} />
      );
      formattedChange = `${changeValue.toLocaleString()} EA`;
    } else {
      color = "#555555";
      icon = <RemoveIcon fontSize="small" style={{ marginRight: "4px" }} />;
      formattedChange = "0 EA";
    }
    return (
      <StockChangeIndicator style={{ color }}>
        {icon}
        {formattedChange}
      </StockChangeIndicator>
    );
  };
  const calculateTotalStock = () => {
    let total = 0;
    totalStockData.forEach((item) => {
      total += Number(item.total_stock_quantity);
    });
    return total;
  };
  const calculateTotalChange = () => {
    let total = 0;
    totalStockData.forEach((item) => {
      total +=
        Number(item.total_stock_quantity) - Number(item.yesterday_quantity);
    });
    return total;
  };
  const handleRefresh = () => {
    setUpdateStatus((prev) => !prev);
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  const columns = [
    {
      accessorKey: "item_code",
      header: "제품 코드",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "total_stock_quantity",
      header: "현재 재고",
      Cell: ({ cell }) => formatQuantity(cell.getValue()),
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "yesterday_quantity",
      header: "전일 재고",
      Cell: ({ cell }) => formatQuantity(cell.getValue()),
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      id: "change",
      header: "증감량",
      Cell: ({ row }) => {
        const change =
          Number(row.original.total_stock_quantity) -
          Number(row.original.yesterday_quantity);
        return renderChangeIndicator(change);
      },
      accessorFn: (row) =>
        Number(row.total_stock_quantity) - Number(row.yesterday_quantity),
      sortingFn: "basic",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      id: "status",
      header: "상태",
      Cell: ({ row }) => {
        const isLow =
          Number(row.original.total_stock_quantity) <
          Number(row.original.min_stock_quantity);
        return (
          <Box
            sx={{
              backgroundColor: isLow ? "#fff4f4" : "#eaf7ea",
              color: isLow ? "#d32f2f" : "#2e7d32",
              borderRadius: "4px",
              padding: "4px 8px",
              display: "inline-block",
              fontWeight: "bold",
              fontSize: "0.75rem",
              textAlign: "center",
            }}
          >
            {isLow ? "부족" : "정상"}
          </Box>
        );
      },
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
  ];
  const getBarChartOptions = () => {
    const sortedData = [...totalStockData].sort(
      (a, b) => Number(b.total_stock_quantity) - Number(a.total_stock_quantity)
    );

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "10%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        name: "수량 (EA)",
        nameLocation: "middle",
        nameGap: 30,
      },
      yAxis: {
        type: "category",
        data: sortedData.map((item) => item.item_code),
        axisLabel: {
          interval: 0,
          rotate: 0,
        },
      },
      series: [
        {
          name: "재고량",
          type: "bar",
          data: sortedData.map((item) => Number(item.total_stock_quantity)),
          itemStyle: {
            color: function (params) {
              const item = sortedData[params.dataIndex];
              return Number(item.total_stock_quantity) <
                Number(item.min_stock_quantity)
                ? "#ff7875"
                : "#52c41a";
            },
          },
          label: {
            show: true,
            position: "right",
            formatter: "{c} EA",
          },
        },
      ],
    };
  };
  const getPieChartOptions = () => {
    return {
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b}: {c} EA ({d}%)",
      },
      legend: {
        type: "scroll",
        orient: "vertical",
        right: 10,
        top: "center",
        padding: [0, 20],
      },
      series: [
        {
          name: "재고 비율",
          type: "pie",
          radius: ["40%", "70%"],
          center: ["40%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: "bold",
            },
          },
          data: totalStockData.map((item) => ({
            value: Number(item.total_stock_quantity),
            name: item.item_code,
          })),
        },
      ],
    };
  };
  const getInStockTrendOptions = () => {
    const { dates, series } = organizeTrendData(inStockTrendData, "in");
    if (dates.length === 0 || series.length === 0) {
      return {
        title: {
          text: "데이터가 없습니다",
          left: "center",
          top: "center",
        },
      };
    }
    return {
      tooltip: {
        trigger: "axis",
      },
      legend: {
        type: "scroll",
        data: series.map((item) => item.name),
        bottom: 10,
        pageButtonPosition: "end",
        pageButtonGap: 5,
        pageButtonItemGap: 5,
        pageIconColor: "#888",
        pageIconInactiveColor: "#aaa",
        pageIconSize: 15,
        pageTextStyle: {
          color: "#888",
        },
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates.map((date) => {
          const dateObj = new Date(date);
          return `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;
        }),
      },
      yAxis: {
        type: "value",
        name: "수량 (EA)",
      },
      series: series,
    };
  };
  const getOutStockTrendOptions = () => {
    const { dates, series } = organizeTrendData(outStockTrendData, "out");
    if (dates.length === 0 || series.length === 0) {
      return {
        title: {
          text: "데이터가 없습니다",
          left: "center",
          top: "center",
        },
      };
    }
    return {
      tooltip: {
        trigger: "axis",
      },
      legend: {
        type: "scroll",
        data: series.map((item) => item.name),
        bottom: 10,
        pageButtonPosition: "end",
        pageButtonGap: 5,
        pageButtonItemGap: 5,
        pageIconColor: "#888",
        pageIconInactiveColor: "#aaa",
        pageIconSize: 15,
        pageTextStyle: {
          color: "#888",
        },
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates.map((date) => {
          const dateObj = new Date(date);
          return `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;
        }),
      },
      yAxis: {
        type: "value",
        name: "수량 (EA)",
      },
      series: series,
    };
  };
  const getStockQuantityTrendOptions = () => {
    const { dates, series } = organizeTrendData(
      stockQuantityTrendData,
      "stock"
    );
    if (dates.length === 0 || series.length === 0) {
      return {
        title: {
          text: "데이터가 없습니다",
          left: "center",
          top: "center",
        },
      };
    }
    return {
      tooltip: {
        trigger: "axis",
      },
      legend: {
        type: "scroll",
        data: series.map((item) => item.name),
        bottom: 10,
        pageButtonPosition: "end",
        pageButtonGap: 5,
        pageButtonItemGap: 5,
        pageIconColor: "#888",
        pageIconInactiveColor: "#aaa",
        pageIconSize: 15,
        pageTextStyle: {
          color: "#888",
        },
      },
      grid: {
        left: "3%",
        right: "3%",
        bottom: "15%",
        top: "8%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: dates.map((date) => {
          const dateObj = new Date(date);
          return `${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;
        }),
      },
      yAxis: {
        type: "value",
        name: "수량 (EA)",
      },
      series: series,
    };
  };
  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              재고 현황
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>
            <Typography variant="body2">시스템 날짜: {systemDate}</Typography>
          </ToolbarRight>
        </ToolbarContainer>
        <DashboardContainer>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} container spacing={2}>
                <Grid item xs={12} md={3}>
                  <StatCard>
                    <StatCardContent>
                      <Typography color="textSecondary" gutterBottom>
                        총 제품 수
                      </Typography>
                      <Typography variant="h4" component="div">
                        {totalStockData.length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        등록된 전체 제품
                      </Typography>
                    </StatCardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatCard>
                    <StatCardContent>
                      <Typography color="textSecondary" gutterBottom>
                        총 재고량
                      </Typography>
                      <Typography variant="h4" component="div">
                        {formatQuantity(calculateTotalStock())}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        모든 제품의 재고 합계
                      </Typography>
                    </StatCardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatCard>
                    <StatCardContent>
                      <Typography color="textSecondary" gutterBottom>
                        일일 재고 증감
                      </Typography>
                      <Box display="flex" alignItems="center">
                        {renderChangeIndicator(calculateTotalChange())}
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        전일 대비 변동량
                      </Typography>
                    </StatCardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatCard
                    sx={{
                      bgcolor: lowStockItems.length > 0 ? "#fff9f9" : "#f5fff5",
                    }}
                  >
                    <StatCardContent>
                      <Typography color="textSecondary" gutterBottom>
                        재고 부족 품목
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="h4"
                          component="div"
                          color={lowStockItems.length > 0 ? "error" : "success"}
                        >
                          {lowStockItems.length}
                        </Typography>
                        {lowStockItems.length > 0 && (
                          <WarningIcon color="error" sx={{ ml: 1 }} />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color={lowStockItems.length > 0 ? "error" : "success"}
                      >
                        {lowStockItems.length > 0
                          ? "주문이 필요한 품목"
                          : "모든 품목 정상 재고"}
                      </Typography>
                    </StatCardContent>
                  </StatCard>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" mb={2}>
                    재고 상세 현황
                  </Typography>
                  <MaterialReactTable
                    columns={columns}
                    data={totalStockData}
                    enableColumnActions={false}
                    enableColumnFilters
                    enableSorting
                    enableTopToolbar={false}
                    enablePagination
                    enableBottomToolbar
                    renderToolbarInternalActions={({ table }) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          height: "1px",
                        }}
                      ></div>
                    )}
                    muiTableBodyRowProps={({ row }) => ({
                      sx: {
                        backgroundColor:
                          Number(row.original.total_stock_quantity) <
                          Number(row.original.min_stock_quantity)
                            ? "rgba(255, 77, 79, 0.05)"
                            : undefined,
                      },
                    })}
                    initialState={{
                      density: "compact",
                      pagination: { pageSize: 10, pageIndex: 0 },
                    }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <ChartContainer>
                  <ChartHeader>
                    <Typography variant="h6">재고 구성 현황</Typography>
                  </ChartHeader>
                  <Divider sx={{ mb: 2 }} />
                  <SplitChartContainer>
                    <BarChartContainer>
                      <ReactECharts
                        option={getBarChartOptions()}
                        style={{ height: "100%", width: "100%" }}
                      />
                    </BarChartContainer>
                    <PieChartContainer>
                      <ReactECharts
                        option={getPieChartOptions()}
                        style={{ height: "100%", width: "100%" }}
                      />
                    </PieChartContainer>
                  </SplitChartContainer>
                </ChartContainer>
              </Grid>
              <Grid item xs={12}>
                <TrendChartContainer>
                  <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="재고량 추이" />
                        <Tab label="입고량 추이" />
                        <Tab label="출고량 추이" />
                      </Tabs>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>기간</InputLabel>
                        <Select
                          value={timeRange}
                          label="기간"
                          onChange={handleTimeRangeChange}
                        >
                          <MenuItem value="week">1주</MenuItem>
                          <MenuItem value="month">1개월</MenuItem>
                          <MenuItem value="quarter">3개월</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  <ChartTabPanel value={tabValue} index={0}>
                    {trendDataLoading ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ReactECharts
                        option={getStockQuantityTrendOptions()}
                        style={{ height: "90%", width: "100%" }}
                        notMerge={true}
                        lazyUpdate={true}
                      />
                    )}
                  </ChartTabPanel>
                  <ChartTabPanel value={tabValue} index={1}>
                    {trendDataLoading ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ReactECharts
                        option={getInStockTrendOptions()}
                        style={{ height: "90%", width: "100%" }}
                        notMerge={true}
                        lazyUpdate={true}
                      />
                    )}
                  </ChartTabPanel>
                  <ChartTabPanel value={tabValue} index={2}>
                    {trendDataLoading ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ReactECharts
                        option={getOutStockTrendOptions()}
                        style={{ height: "90%", width: "100%" }}
                        notMerge={true}
                        lazyUpdate={true}
                      />
                    )}
                  </ChartTabPanel>
                </TrendChartContainer>
              </Grid>
            </Grid>
          )}
        </DashboardContainer>
      </FrameContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default Stock;
